import { clearStoredTenantConfig } from "./tenantConfig";
import { clearTimingCache } from "./attendanceTimingCache";
import { clearPatientTabsCache } from "../pages/Patients/usePatientTabs";
import { clearPromoLoginFlag } from "../components/Promo/promoCampaignStorage";

export const CURRENT_USER_STORAGE_KEY = "currentUser";
export const SESSION_USER_CHANGED_EVENT = "hrms:current-user-changed";

/** Application form draft keys that may contain tenant-scoped data. */
const FORM_HISTORY_KEYS = [
  "rnApplicationHistory",
  "tbHistory",
  "routeHistory",
  "consentHistory",
  "medicareHistory",
  "fluHistory",
  "healthHistory",
  "applicationHistory",
];

/**
 * Read parsed session from localStorage (no throw).
 */
export function readCurrentUserSession() {
  try {
    return JSON.parse(localStorage.getItem(CURRENT_USER_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

/**
 * Resolve tenantId from the logged-in session.
 * @returns {number|null}
 */
export function getSessionTenantId() {
  const session = readCurrentUserSession();
  const tenantId = session.tenantId ?? session.tenant?.id;
  if (tenantId == null || tenantId === "") return null;
  const parsed = Number(tenantId);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Same as getSessionTenantId but throws when missing (for API calls).
 */
export function requireSessionTenantId() {
  const tenantId = getSessionTenantId();
  if (!tenantId) {
    throw new Error("Tenant context is missing. Please sign in again.");
  }
  return tenantId;
}

/**
 * Resolve tenantId for form submit payloads — session first, never falls back to 1.
 * @returns {number|undefined}
 */
export function resolveFormTenantId(explicitTenantId) {
  const fromSession = getSessionTenantId();
  if (fromSession) return fromSession;
  if (explicitTenantId != null && explicitTenantId !== "") {
    const parsed = Number(explicitTenantId);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return undefined;
}

/** Shared placeholder for react-hook-form defaults (set from session on mount). */
export const FORM_DEFAULT_TENANT_ID = undefined;
export const FORM_DEFAULT_USER_ID = undefined;

/**
 * Resolve user id from session.
 */
export function getSessionUserId() {
  const session = readCurrentUserSession();
  const id = session.id ?? session.userId;
  if (id == null || id === "") return null;
  const parsed = Number(id);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Persist login session and notify hooks in the same tab.
 */
export function persistCurrentUserSession(payload) {
  localStorage.setItem(CURRENT_USER_STORAGE_KEY, JSON.stringify(payload));
  try {
    window.dispatchEvent(new Event(SESSION_USER_CHANGED_EVENT));
  } catch {
    // ignore SSR / test env
  }
}

/**
 * Clear all tenant/session caches on logout or invalid token.
 */
export function clearAllTenantSessionData() {
  try {
    localStorage.removeItem(CURRENT_USER_STORAGE_KEY);
  } catch {
    // ignore
  }
  clearStoredTenantConfig();
  clearTimingCache();
  clearPatientTabsCache();
  FORM_HISTORY_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  });
  clearPromoLoginFlag();
  try {
    window.dispatchEvent(new Event(SESSION_USER_CHANGED_EVENT));
  } catch {
    // ignore
  }
}
